import { useEffect } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useOrgQuery } from '@/lib/hooks/useOrgQuery'
import {
  Boxes,
  Database,
  Gauge,
  Inbox,
  KeyRound,
  LogOut,
  PackagePlus,
  Settings,
} from 'lucide-react'
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { useAuth } from '@/lib/auth/useAuth'
import { EcosystemMark } from '@/components/hawk-primitives'

interface RepoMeta {
  name: string
  ecosystem: string
}

interface RequestMeta {
  id: string
  package_name: string
  package_version: string | null
  repository_name: string
  ecosystem: string
  status: string
  admission_status: string | null
}

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function CommandPalette({ open, onOpenChange }: Props) {
  const navigate = useNavigate()
  const { logout, hasOrgRole, currentOrgSlug } = useAuth()
  const isAdmin = hasOrgRole('admin')

  const repos = useOrgQuery<RepoMeta[]>(['admin-repositories'], '/admin/repositories', {
    enabled: open && isAdmin,
    staleTime: 60_000,
  })

  const requests = useOrgQuery<RequestMeta[]>(
    ['admin-requests-open'],
    '/admin/requests?limit=50',
    { enabled: open && isAdmin, staleTime: 15_000 },
  )

  // Auto-refetch the live lists each time the palette opens so we don't show
  // stale results after a session of interactions.
  useEffect(() => {
    if (open) {
      repos.refetch()
      requests.refetch()
    }
    // intentionally only on `open` transitions
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  type StaticPath =
    | '/dashboard'
    | '/packages'
    | '/org'
    | '/org/repositories'
    | '/org/requests'
    | '/settings'
    | '/settings/security'

  function go(to: StaticPath) {
    onOpenChange(false)
    navigate({ to })
  }

  function goToRepo(repo: string) {
    onOpenChange(false)
    navigate({ to: '/packages/$repo', params: { repo } })
  }

  const repoList = Array.isArray(repos.data) ? repos.data : []
  const requestList = Array.isArray(requests.data) ? requests.data : []
  const openRequests = requestList.filter((r) =>
    ['pending', 'verified', 'unverified'].includes(r.status),
  )

  return (
    <CommandDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Search"
      description="Jump to a page, repository, request, or run an action."
    >
      <CommandInput placeholder="Search pages, repositories, requests…" />
      <CommandList>
        <CommandEmpty>No matches.</CommandEmpty>

        <CommandGroup heading="Pages">
          <CommandItem
            value="dashboard home overview"
            onSelect={() => go('/dashboard')}
          >
            <Gauge strokeWidth={1.5} />
            Dashboard
          </CommandItem>
          <CommandItem
            value="packages browse repos"
            onSelect={() => go('/packages')}
          >
            <Boxes strokeWidth={1.5} />
            Packages
          </CommandItem>
          {isAdmin && (
            <>
              <CommandItem
                value="admin operator console overview"
                onSelect={() => go('/org')}
              >
                <Gauge strokeWidth={1.5} />
                Operator console
              </CommandItem>
              <CommandItem
                value="repositories manage repo"
                onSelect={() => go('/org/repositories')}
              >
                <Database strokeWidth={1.5} />
                Repositories
              </CommandItem>
              <CommandItem
                value="package requests inbox review"
                onSelect={() => go('/org/requests')}
              >
                <Inbox strokeWidth={1.5} />
                Package requests
              </CommandItem>
            </>
          )}
          <CommandItem
            value="profile settings account identity"
            onSelect={() => go('/settings')}
          >
            <Settings strokeWidth={1.5} />
            Profile
          </CommandItem>
          <CommandItem
            value="security webauthn keys sessions"
            onSelect={() => go('/settings/security')}
          >
            <KeyRound strokeWidth={1.5} />
            Security keys & sessions
          </CommandItem>
        </CommandGroup>

        {repoList.length > 0 && (
          <CommandGroup heading="Repositories">
            {repoList.map((r) => (
              <CommandItem
                key={r.name}
                value={`repo ${r.name} ${r.ecosystem}`}
                onSelect={() => goToRepo(r.name)}
              >
                <EcosystemMark ecosystem={r.ecosystem} size="sm" />
                <span className="flex-1 truncate">{r.name}</span>
                <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                  {r.ecosystem}
                </span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {openRequests.length > 0 && (
          <CommandGroup heading="Open requests">
            {openRequests.map((r) => (
              <CommandItem
                key={r.id}
                value={`request ${r.package_name} ${r.package_version ?? ''} ${r.repository_name}`}
                onSelect={() => go('/org/requests')}
              >
                <EcosystemMark ecosystem={r.ecosystem} size="sm" />
                <span className="flex-1 truncate">
                  {r.package_name}
                  {r.package_version && (
                    <span className="text-primary ml-1.5 font-mono text-xs">
                      {r.package_version}
                    </span>
                  )}
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground truncate max-w-[140px]">
                  in {r.repository_name}
                </span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        <CommandGroup heading="Actions">
          <CommandItem
            value="request package new install"
            onSelect={() => go('/packages')}
          >
            <PackagePlus strokeWidth={1.5} />
            Request a package
          </CommandItem>
          <CommandItem
            value="sign out logout leave"
            onSelect={() => {
              onOpenChange(false)
              logout()
            }}
          >
            <LogOut strokeWidth={1.5} />
            Sign out
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  )
}
